/**
 * Energy VAD (Voice Activity Detection) Hook
 * 基于音量阈值的通用声音活动检测
 * 
 * 与 Silero VAD 不同，这个 VAD 不区分声音类型，
 * 只检测是否有声音活动，适用于 Pet ASR 等需要检测非人声的场景
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export interface EnergyVADOptions {
  /** 语音开始回调 */
  onSpeechStart?: () => void
  /** 语音结束回调，返回音频数据 */
  onSpeechEnd?: (audio: Float32Array, duration: number) => void
  /** VAD 状态变化回调（未检测到有效声音） */
  onVADMisfire?: () => void
  /** 错误回调 */
  onError?: (error: string) => void
  /** 采样率，默认 16000 */
  sampleRate?: number
  /** 能量阈值（RMS），默认 0.01，值越小越敏感 */
  energyThreshold?: number
  /** 静音超时时间(ms)，默认 800，超过此时间无声音视为语音结束 */
  silenceTimeoutMs?: number
  /** 最小语音时长(ms)，默认 200 */
  minSpeechMs?: number
  /** 分析间隔(ms)，默认 50 */
  analyzeIntervalMs?: number
}

export interface EnergyVADState {
  /** VAD 是否已初始化 */
  isInitialized: boolean
  /** VAD 是否正在监听 */
  isListening: boolean
  /** 当前是否检测到语音 */
  isSpeaking: boolean
  /** 是否暂停（用于 AEC） */
  isPaused: boolean
  /** 浏览器是否支持 */
  isSupported: boolean
  /** 模型是否正在加载（能量 VAD 不需要加载模型，始终为 false） */
  isLoading: boolean
  /** 错误信息 */
  error: string | null
}

/**
 * 计算音频数据的 RMS（均方根）能量
 */
function calculateRMS(audioData: Float32Array): number {
  let sum = 0
  for (let i = 0; i < audioData.length; i++) {
    sum += audioData[i] * audioData[i]
  }
  return Math.sqrt(sum / audioData.length)
}

/**
 * Energy VAD Hook
 * 
 * @example
 * ```tsx
 * const { isListening, isSpeaking, start, stop, pause, resume } = useEnergyVAD({
 *   onSpeechStart: () => console.log('检测到声音'),
 *   onSpeechEnd: (audio) => sendToServer(audio),
 *   energyThreshold: 0.01,  // 调低可检测更小的声音
 * })
 * ```
 */
export function useEnergyVAD(options: EnergyVADOptions = {}) {
  const {
    onSpeechStart,
    onSpeechEnd,
    onVADMisfire,
    onError,
    sampleRate = 16000,
    energyThreshold = 0.01,
    silenceTimeoutMs = 800,
    minSpeechMs = 200,
    analyzeIntervalMs: _analyzeIntervalMs = 50,
  } = options

  const [state, setState] = useState<EnergyVADState>({
    isInitialized: false,
    isListening: false,
    isSpeaking: false,
    isPaused: false,
    isSupported: typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
    isLoading: false,
    error: null,
  })

  // Refs
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const analyzeIntervalRef = useRef<number | null>(null)

  // 语音检测状态
  const isSpeakingRef = useRef(false)
  const speechStartTimeRef = useRef<number>(0)
  const lastSoundTimeRef = useRef<number>(0)
  const audioBufferRef = useRef<Float32Array[]>([])
  const isPausedRef = useRef(false)

  // 初始化（能量 VAD 不需要加载模型）
  const initialize = useCallback(async () => {
    if (!state.isSupported) {
      const errorMsg = '浏览器不支持麦克风访问'
      setState(prev => ({ ...prev, error: errorMsg }))
      onError?.(errorMsg)
      return false
    }

    setState(prev => ({ ...prev, isInitialized: true }))
    return true
  }, [state.isSupported, onError])

  // 停止分析
  const stopAnalyzing = useCallback(() => {
    if (analyzeIntervalRef.current) {
      clearInterval(analyzeIntervalRef.current)
      analyzeIntervalRef.current = null
    }
  }, [])

  // 清理音频资源
  const cleanupAudio = useCallback(() => {
    stopAnalyzing()

    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect()
      scriptProcessorRef.current = null
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect()
      workletNodeRef.current = null
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect()
      analyserRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
  }, [stopAnalyzing])

  // 开始监听
  const start = useCallback(async () => {
    // 先初始化
    if (!state.isInitialized) {
      const success = await initialize()
      if (!success) return false
    }

    // 检查是否支持 mediaDevices
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errorMsg = '麦克风 API 不可用。请使用 localhost 访问，或在 Chrome 中设置 chrome://flags → "Insecure origins treated as secure" 添加当前地址'
      setState(prev => ({ ...prev, error: errorMsg }))
      onError?.(errorMsg)
      return false
    }

    try {
      // 获取麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: false,  // 关闭降噪，保留更多声音细节
          autoGainControl: true,
        }
      })
      streamRef.current = stream

      // 创建音频上下文
      const audioContext = new AudioContext({ sampleRate })
      audioContextRef.current = audioContext

      const source = audioContext.createMediaStreamSource(stream)

      // 创建分析器
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      analyser.smoothingTimeConstant = 0.3
      source.connect(analyser)
      analyserRef.current = analyser

      // 使用 ScriptProcessorNode 收集音频数据（虽然已废弃，但兼容性更好）
      const bufferSize = 4096
      const scriptProcessor = audioContext.createScriptProcessor(bufferSize, 1, 1)
      source.connect(scriptProcessor)
      scriptProcessor.connect(audioContext.destination)
      scriptProcessorRef.current = scriptProcessor

      // 处理音频数据
      scriptProcessor.onaudioprocess = (event) => {
        if (isPausedRef.current) return

        const inputData = event.inputBuffer.getChannelData(0)
        const rms = calculateRMS(inputData)
        const now = Date.now()

        if (rms > energyThreshold) {
          // 检测到声音
          lastSoundTimeRef.current = now

          if (!isSpeakingRef.current) {
            // 语音开始
            isSpeakingRef.current = true
            speechStartTimeRef.current = now
            audioBufferRef.current = []
            setState(prev => ({ ...prev, isSpeaking: true }))
            onSpeechStart?.()
            console.log('[EnergyVAD] 🎤 检测到声音开始, RMS:', rms.toFixed(4))
          }

          // 收集音频数据
          audioBufferRef.current.push(new Float32Array(inputData))
        } else if (isSpeakingRef.current) {
          // 静音期间也收集数据（用于边界）
          audioBufferRef.current.push(new Float32Array(inputData))

          // 检查是否超过静音超时
          if (now - lastSoundTimeRef.current > silenceTimeoutMs) {
            // 语音结束
            const duration = now - speechStartTimeRef.current
            isSpeakingRef.current = false
            setState(prev => ({ ...prev, isSpeaking: false }))

            console.log('[EnergyVAD] 🔇 声音结束, 时长:', duration, 'ms')

            if (duration >= minSpeechMs) {
              // 合并音频缓冲区
              const totalLength = audioBufferRef.current.reduce((acc, buf) => acc + buf.length, 0)
              const mergedAudio = new Float32Array(totalLength)
              let offset = 0
              for (const buf of audioBufferRef.current) {
                mergedAudio.set(buf, offset)
                offset += buf.length
              }

              onSpeechEnd?.(mergedAudio, duration)
            } else {
              console.log('[EnergyVAD] 声音太短，忽略')
              onVADMisfire?.()
            }

            audioBufferRef.current = []
          }
        }
      }

      isPausedRef.current = false
      setState(prev => ({ ...prev, isListening: true, isPaused: false, error: null }))
      console.log('[EnergyVAD] ✅ 开始监听, 能量阈值:', energyThreshold)
      return true
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '启动失败'
      setState(prev => ({ ...prev, error: errorMsg }))
      onError?.(errorMsg)
      cleanupAudio()
      return false
    }
  }, [
    state.isInitialized,
    initialize,
    sampleRate,
    energyThreshold,
    silenceTimeoutMs,
    minSpeechMs,
    onSpeechStart,
    onSpeechEnd,
    onVADMisfire,
    onError,
    cleanupAudio,
  ])

  // 停止监听
  const stop = useCallback(() => {
    cleanupAudio()
    isSpeakingRef.current = false
    audioBufferRef.current = []
    setState(prev => ({
      ...prev,
      isListening: false,
      isSpeaking: false,
      isPaused: false,
    }))
    console.log('[EnergyVAD] ⏹️ 停止监听')
  }, [cleanupAudio])

  // 暂停（用于 AEC - 播放 TTS 时暂停）
  const pause = useCallback(() => {
    if (state.isListening) {
      isPausedRef.current = true
      setState(prev => ({ ...prev, isPaused: true, isSpeaking: false }))
      isSpeakingRef.current = false
      console.log('[EnergyVAD] ⏸️ 暂停')
    }
  }, [state.isListening])

  // 恢复
  const resume = useCallback(() => {
    if (state.isPaused) {
      isPausedRef.current = false
      setState(prev => ({ ...prev, isPaused: false }))
      console.log('[EnergyVAD] ▶️ 恢复')
    }
  }, [state.isPaused])

  // 销毁
  const destroy = useCallback(() => {
    stop()
    setState(prev => ({
      ...prev,
      isInitialized: false,
    }))
  }, [stop])

  // 清理
  useEffect(() => {
    return () => {
      cleanupAudio()
    }
  }, [cleanupAudio])

  return {
    ...state,
    initialize,
    start,
    stop,
    pause,
    resume,
    destroy,
  }
}
